import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { ProductTypeResponse, ProductTypeRequest, ProductTypesService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-product-type',
    templateUrl: './edit-product-type.component.html',
    styleUrl: './edit-product-type.component.scss',
    host: {
        class: 'app-edit-product-type'
    },
    standalone: false
})
export class EditProductTypeComponent extends BaseViewComponent implements OnInit {

  private readonly destroyedComponent$ = new Subject<void>();

  productTypeId: number = 0;
  productType: ProductTypeResponse = {};

  filterOperator = FilterOperator;
  productTypesList: any[] = [];
  productTypesTotalRecords: number = 0;
  productTypesRowsPerPageOption: number = 10;

  public productTypeForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(100)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    hasParentProductType: ['false'],
    productType: [[]],
    imageUrl: [null]
  }, { validators: this.conditionalValidators() });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productTypesService: ProductTypesService,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.productTypeId = params['productTypeId'];

      if (this.productTypeId) {
        this.productType = await firstValueFrom(this.productTypesService.apiProductTypesIdGet(this.productTypeId));

        this.initForm();
        await this.getProductTypes({ pageIndex: 1, pageSize: this.productTypesRowsPerPageOption, search: ''});
      }
    });
  }

  isProductTypeRequired(): boolean {
    return this.productTypeForm.get('hasParentProductType')?.value === 'true';
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const hasParentProductType = formGroup.get('hasParentProductType')?.value;
      const productType = formGroup.get('productType')?.value ?? [];

      if (hasParentProductType === 'true' && productType.length === 0) {
        return { requiredproductType: true };
      }

      return null;
    };
  }

  async onLazyLoadProductTypes(event: any) {
    await this.getProductTypes(event);
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  async getProductTypes(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: event?.pageSize ?? this.productTypesRowsPerPageOption
    }

    if (search) {
      payload.textFilter = search;
    }

    const productTypesPage = await firstValueFrom(this.productTypesService.apiProductTypesFilterComboPost(payload));

    // prevent adding itself as parent
    this.productTypesList = (productTypesPage.results ?? []).filter(c => c.id != this.productTypeId);

    this.productTypesTotalRecords = productTypesPage.totalCount ?? 0;

    if (this.productType?.parent?.id && this.productType.parent.id != this.productTypeId) {
      const exists = this.productTypesList.some((c: any) => c.id == this.productType.parent?.id);
      if (!exists) {
        const current = await firstValueFrom(this.productTypesService.apiProductTypesComboValueIdGet(this.productType.parent?.id))
        this.productTypesList = [current, ...this.productTypesList];
      }
    }

  }

  initForm() {
    this.productTypeForm.controls['name'].setValue(this.productType.name);
    this.productTypeForm.controls['code'].setValue(this.productType.code);
    this.productTypeForm.controls['imageUrl'].setValue(this.productType.imageUrl);

    if (this.productType.parentId) {
      this.productTypeForm.controls['hasParentProductType'].setValue('true');
      this.productTypeForm.controls['productType'].setValue([this.productType.parentId]);
    } else {
      this.productTypeForm.controls['hasParentProductType'].setValue('false');
      this.productTypeForm.controls['productType'].setValue([]);
    }

    this.productTypeForm.get('hasParentProductType')?.valueChanges.subscribe((hasParent) => {
      if (hasParent === 'false') {
        this.productTypeForm.controls['productType'].setValue([]);
      }
    });
  }

  async submitForm() {
    if (this.productTypeForm.valid) {

      const payload: ProductTypeRequest = {
        name: this.productTypeForm.controls['name'].value,
        code: this.productTypeForm.controls['code'].value,
        parentId: this.productTypeForm.controls['productType'].value?.[0],
        imageUrl: this.productTypeForm.controls['imageUrl'].value
      };

      await firstValueFrom(this.productTypesService.apiProductTypesUpdateIdPut(this.productTypeId, payload));

      this.router.navigate(['/product-types', this.productTypeId]);
    }
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
